import { useState, useEffect } from "react"

import { RateReview } from '@mui/icons-material'
import {
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material'
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import { useNavigate } from "react-router-dom"

import { aiQcService, handleApiError, type AIQCReview } from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { DateCell, LinkCell, StatusChip } from "../components/data-grid-cells"
import { truncateId } from "../components/data-grid-utils"
import { useUrlPaginationModel } from "../hooks/use-url-pagination-model"

export default function AIQCPendingPage() {
  const [pendingReviews, setPendingReviews] = useState<AIQCReview[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const { paginationModel, onPaginationModelChange } = useUrlPaginationModel()

  const loadReviews = async () => {
    try {
      const pending = await aiQcService.getPendingReviews()
      setPendingReviews(pending)
      setError("")
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReviews()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning' as const
      case 'approved': return 'success' as const
      case 'rejected': return 'error' as const
      default: return 'default' as const
    }
  }

  const columns: GridColDef<AIQCReview>[] = [
    {
      field: 'userEmail',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row?.session?.user?.email,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const user = params.row?.session?.user
        if (!user) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }
        return (
          <Box sx={{ py: 0.5 }}>
            <LinkCell to={`/users/${user.id}`}>
              {user.email}
            </LinkCell>
            {user.firstName && user.lastName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {user.firstName} {user.lastName}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      flex: 0.8,
      minWidth: 160,
      valueGetter: (_, row) => {
        const metadata = row?.session?.metadata
        return metadata?.junctionLabOrderId || metadata?.labOrderId
      },
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const metadata = params.row?.session?.metadata
        const labOrderId = metadata?.labOrderId
        const orderId = metadata?.junctionLabOrderId || labOrderId

        if (!orderId) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }

        const linkTarget = labOrderId || metadata?.junctionLabOrderId
        return (
          <LinkCell to={`/orders/${linkTarget}`} monospace>
            {truncateId(orderId)}
          </LinkCell>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <StatusChip
          label={params.row.status.toUpperCase()}
          color={getStatusColor(params.row.status)}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => <DateCell value={params.row.createdAt} stacked />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<RateReview />}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/ai-qc/review/${params.row.id}`)
          }}
        >
          Review
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <Box sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 3 }}>
            Pending Review
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 0 }}>
              {pendingReviews.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No pending reviews at this time.
                  </Typography>
                </Box>
              ) : (
                <AdminDataGrid
                  rows={pendingReviews}
                  columns={columns}
                  rowHeight={80}
                  paginationModel={paginationModel}
                  onPaginationModelChange={onPaginationModelChange}
                  onRowClick={(params) => navigate(`/ai-qc/review/${params.id}`)}
                />
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Box>
  )
}
